import { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Hash, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { PhoneInput, parsePhoneString } from '@/components/ui/PhoneInput';
import { DateInput } from '@/components/ui/DateInput';
import { DuplicateWarningModal } from '@/components/leads/DuplicateWarningModal';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';
import { getCountryNames, getStateNames, getCityNames, countryIdByName, stateIdByName, cityIdByName } from '@/lib/geoApi';
import { SelectField } from '@/components/ui/SelectField';

// Lead sources now come from the tenant-scoped /leads/sources API which
// merges super-admin defaults with tenant's own entries. Hardcoded list
// kept only as a hydration fallback for the (rare) case where the API
// errors out before the user opens the form.
const FALLBACK_SOURCE_OPTIONS = [
  'Website', 'Facebook', 'Google', 'LinkedIn', 'Instagram',
  'Organic', 'Referral', 'Manual', 'WhatsApp', 'API', 'CSV Import', 'Other',
];
const CATEGORY_OPTIONS = ['Hot', 'Warm', 'Cold'];
const COLOR_OPTIONS = ['Gray', 'Red', 'Green', 'Blue', 'Yellow'];
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];

const INPUT = 'w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] placeholder:text-gray-400';
const SELECT = `${INPUT} appearance-none cursor-pointer`;
const LABEL = 'block text-xs font-medium text-[#64748B] mb-1';
const SECTION_TITLE = 'text-sm font-semibold text-[#6366F1] mb-4';

// ── Geo API helpers ────────────────────────────────────────────────────────────
//
// Country / state / city all come from our own DB master via lib/geoApi
// (GET /api/geo/*). No more static list or countriesnow.space.
const fetchStates = getStateNames;
const fetchCities = getCityNames;

// ── Component ──────────────────────────────────────────────────────────────────

interface User { id: string; name: string; }
interface CustomFieldDef {
  id: string;
  label: string;
  fieldType: string;
  keyValues: any[];
  isRequired: boolean;
  isActive: boolean;
  entityType: string;
}
interface Props {
  lead?: any;
  onClose: () => void;
  onSaved: () => void;
  onPlanLimit?: (message: string) => void;
}

export function LeadFormModal({ lead, onClose, onSaved, onPlanLimit }: Props) {
  const isEdit = !!lead;
  const [users, setUsers] = useState<User[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [sourceOptions, setSourceOptions] = useState<string[]>(FALLBACK_SOURCE_OPTIONS);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [hashtagInput, setHashtagInput] = useState('');
  const [showUtm, setShowUtm] = useState(false);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDef[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

  // Geo state
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [geoError, setGeoError] = useState('');

  // Abort refs to cancel in-flight requests on fast changes
  const statesAbort = useRef<AbortController | null>(null);
  const citiesAbort = useRef<AbortController | null>(null);

  const [form, setForm] = useState({
    // Fall back to splitting `name` when a lead was created without
    // first/last (CSV import, public webform, etc.) — otherwise the Edit
    // drawer showed empty name fields for every legacy lead.
    firstName: (() => {
      if (lead?.firstName) return lead.firstName;
      const parts = String(lead?.name || '').trim().split(/\s+/);
      return parts[0] || '';
    })(),
    lastName: (() => {
      if (lead?.lastName) return lead.lastName;
      const parts = String(lead?.name || '').trim().split(/\s+/);
      return parts.length > 1 ? parts.slice(1).join(' ') : '';
    })(),
    emails: (() => {
      // Backend now returns LeadEmail[] objects with .email field. Handle
      // both new shape and legacy string array for safety during transition.
      const raw = Array.isArray(lead?.emails) ? lead.emails : [];
      const list = raw.map((e: any) => (typeof e === 'object' && e?.email ? e.email : String(e || '')));
      return list.length ? list : [''];
    })(),
    phones: (() => {
      const raw = Array.isArray(lead?.phones) ? lead.phones : [];
      if (!raw.length) return [{ countryCode: '+91', number: '' }];
      return raw.map((p: any) => {
        // New LeadPhone shape: { isdCode: "+91", phoneNumber: "98765 43210", ... }
        if (typeof p === 'object' && (p?.isdCode || p?.phoneNumber)) {
          return { countryCode: p.isdCode || '+91', number: p.phoneNumber || '' };
        }
        if (typeof p === 'object' && p?.countryCode) return p;
        if (typeof p === 'object' && p?.phone) return parsePhoneString(p.phone); // legacy
        if (typeof p === 'string') return parsePhoneString(p);
        return { countryCode: '+91', number: '' };
      });
    })(),
    address: lead?.address || '',
    country: lead?.country || '',
    state: lead?.state || '',
    city: lead?.city || '',
    pincode: lead?.pincode || '',
    status: lead?.status || 'New',
    source: lead?.source || '',
    assignedToId: lead?.assignedTo?.id || '',
    leadCategory: lead?.leadCategory || '',
    leadColor: lead?.leadColor || '',
    priority: lead?.priority || '',
    hashtags: (lead?.hashtags || []) as string[],
    notes: lead?.notes || '',
    utmSource: lead?.utmSource || '',
    utmMedium: lead?.utmMedium || '',
    utmCampaign: lead?.utmCampaign || '',
    utmTerm: lead?.utmTerm || '',
    utmContent: lead?.utmContent || '',
    campaignName: lead?.campaignName || '',
  });

  const set = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  // Load countries from the DB geo master on mount.
  useEffect(() => {
    setLoadingCountries(true);
    getCountryNames()
      .then(setCountries)
      .catch(() => setGeoError('Failed to load countries'))
      .finally(() => setLoadingCountries(false));
  }, []);

  // Load users on mount.
  // /users returns every active user in the tenant. /users/managers
  // was restrictive — leads can be assigned to any role (counselor,
  // agent, admin) and missing IDs made the <SelectField> silently fall back
  // to "Unassigned" when editing an already-assigned lead.
  useEffect(() => {
    api.get('/users')
      .then(r => {
        // /users may return { users, total } or a plain array depending
        // on the server — normalize defensively.
        const raw = r.data;
        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.users) ? raw.users : []);
        // Keep only active users to avoid showing deactivated names.
        setUsers(list.filter((u: any) => u.isActive !== false));
      })
      .catch(() => {});
  }, []);

  // Load lead statuses + sources on mount. Sources come from the merged
  // system + tenant list so every vendor sees the super-admin defaults
  // plus anything they've added themselves.
  useEffect(() => {
    api.get('/leads/statuses')
      .then(({ data }) => setStatusOptions(
        (Array.isArray(data) ? data : [])
          .filter((s: any) => s.isActive)
          .map((s: any) => s.name)
      ))
      .catch(() => setStatusOptions(['New', 'Contacted', 'Interested', 'Not Interested', 'Converted', 'Lost']));

    api.get('/leads/sources')
      .then(({ data }) => {
        const names = (Array.isArray(data) ? data : [])
          .filter((s: any) => s.isActive)
          .map((s: any) => s.name);
        if (names.length) setSourceOptions(names);
      })
      .catch(() => {/* keep FALLBACK_SOURCE_OPTIONS */});
  }, []);

  // Load custom fields on mount
  useEffect(() => {
    api.get('/settings/custom-fields')
      .then(r => {
        const defs: CustomFieldDef[] = (Array.isArray(r.data) ? r.data : [])
          .filter((f: CustomFieldDef) => f.entityType === 'LEAD' && f.isActive);
        setCustomFieldDefs(defs);
        const initial: Record<string, any> = {};
        defs.forEach(f => {
          const existing = lead?.customFields?.[f.id];
          if (f.fieldType === 'multi-select') {
            initial[f.id] = Array.isArray(existing) ? existing : [];
          } else if (f.fieldType === 'checkbox') {
            initial[f.id] = existing ?? false;
          } else {
            initial[f.id] = existing ?? '';
          }
        });
        setCustomFieldValues(initial);
      })
      .catch(() => {});
  }, []);

  // Load states when country changes.
  // Some countries (Åland Islands, Vatican City, small island nations)
  // have no administrative states in the data source. For these we fall
  // back to using the country itself as the only state option so the
  // user can still complete the form instead of hitting a dead end.
  useEffect(() => {
    if (!form.country) {
      setStates([]);
      setCities([]);
      return;
    }
    statesAbort.current?.abort();
    setLoadingStates(true);
    setStates([]);
    setCities([]);
    setGeoError('');

    fetchStates(form.country)
      .then((list) => {
        if (list.length > 0) {
          setStates(list);
        } else {
          // Empty list — treat the country as its own single "state".
          setStates([form.country]);
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        // Network failure — degrade gracefully with the same fallback.
        setStates([form.country]);
      })
      .finally(() => setLoadingStates(false));
  }, [form.country]);

  // Load cities when state changes. Same fallback policy as states —
  // if the provider returns nothing, use the state name as the sole
  // city option so the user can still save the lead.
  useEffect(() => {
    if (!form.country || !form.state) {
      setCities([]);
      return;
    }
    citiesAbort.current?.abort();
    setLoadingCities(true);
    setCities([]);
    setGeoError('');

    fetchCities(form.country, form.state)
      .then((list) => {
        if (list.length > 0) {
          setCities(list);
        } else {
          setCities([form.state]);
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setCities([form.state]);
      })
      .finally(() => setLoadingCities(false));
  }, [form.country, form.state]);

  const handleCountryChange = (val: string) => {
    setForm(f => ({ ...f, country: val, state: '', city: '' }));
  };

  const handleStateChange = (val: string) => {
    setForm(f => ({ ...f, state: val, city: '' }));
  };

  // Email helpers
  const addEmail = () => set('emails', [...form.emails, '']);
  const removeEmail = (i: number) => set('emails', form.emails.filter((_: any, idx: number) => idx !== i));
  const updateEmail = (i: number, v: string) => set('emails', form.emails.map((e: string, idx: number) => idx === i ? v : e));

  // Phone helpers
  const addPhone = () => set('phones', [...form.phones, { countryCode: '+91', number: '' }]);
  const removePhone = (i: number) => set('phones', form.phones.filter((_: any, idx: number) => idx !== i));
  // Patch the full phone at index `i` in a single setState. The previous
  // field-by-field implementation had two back-to-back setState calls
  // both closing over the same stale form.phones, so setting a new
  // countryCode + number at once silently dropped one of the two
  // updates (the "+91 still selected after Nepal click" bug).
  const updatePhone = (i: number, patch: { countryCode?: string; number?: string }) =>
    setForm((f: any) => ({
      ...f,
      phones: f.phones.map((p: any, idx: number) => idx === i ? { ...p, ...patch } : p),
    }));

  // Hashtag helpers
  const addHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, '');
    if (tag && !form.hashtags.includes(tag)) {
      set('hashtags', [...form.hashtags, tag]);
      setHashtagInput('');
    }
  };
  const removeHashtag = (tag: string) => set('hashtags', form.hashtags.filter((t: string) => t !== tag));

  const handleSubmit = async () => {
    setError('');
    setIsSaving(true);
    try {
      // Send phones as split { isdCode, phoneNumber } objects (the backend
      // combines + normalises them). phoneStrs is only for the dup-check query.
      const phones = form.phones
        .filter((p: any) => p.number.trim())
        .map((p: any) => ({ isdCode: p.countryCode, phoneNumber: p.number.trim() }));
      const phoneStrs = phones.map((p: any) => `${p.isdCode} ${p.phoneNumber}`.trim());
      const emails = form.emails.filter((e: string) => e.trim());
      const payload = {
        ...form,
        phones,
        emails,
        assignedToId: form.assignedToId || undefined,
        name: `${form.firstName} ${form.lastName}`.trim() || emails[0] || 'Unknown',
        customFields: customFieldValues,
        // Master geo ids (names also sent above as fallback for the backend).
        countryId: countryIdByName(form.country) ?? undefined,
        stateId: stateIdByName(form.country, form.state) ?? undefined,
        cityId: cityIdByName(form.country, form.state, form.city) ?? undefined,
      };
      if (isEdit) {
        await api.patch(`/leads/${lead.id}`, payload);
        toast('success', 'Lead updated');
        onSaved();
      } else {
        // Check for duplicates before creating
        if (!pendingPayload) {
          const params = new URLSearchParams();
          if (phoneStrs.length) params.set('phones', phoneStrs.join(','));
          if (emails.length) params.set('emails', emails.join(','));
          if (params.toString()) {
            const { data: dups } = await api.get(`/leads/check-duplicates?${params}`);
            if (dups && dups.length > 0) {
              setDuplicates(dups);
              setPendingPayload(payload);
              setIsSaving(false);
              return;
            }
          }
        }
        const { data: createRes } = await api.post('/leads', pendingPayload || payload);
        setPendingPayload(null);
        // Backend returns { lead, merged, masterLeadId, duplicateLeadId }.
        // When auto-merge is on and the new lead matched an existing one,
        // the master row was updated instead of a new row being created —
        // surface that to the user so they understand why the list didn't grow.
        if (createRes?.merged) {
          const masterName = createRes?.lead?.name || 'an existing lead';
          toast('success', `Duplicate detected — merged into ${masterName}`);
        } else {
          toast('success', 'Lead created');
        }
        onSaved();
      }
    } catch (err: any) {
      const rawMsg = err?.response?.data?.message;
      const msg = extractErrorMessage(err, 'Failed to save lead');
      // Plan-limit upsell intercept stays — extractErrorMessage flattens
      // arrays/strings consistently so the substring check is reliable.
      if (err?.response?.status === 403 && msg.toLowerCase().includes('upgrade')) {
        onPlanLimit?.(msg);
        onClose();
        return;
      }
      setError(msg);
      toast('error', msg);
      // unused locally but keep ref so a future .message-shape change is greppable
      void rawMsg;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} />

      {/* Sliding panel — full width on mobile, fixed 780px on sm+.
          Wider than before so Country/State/City columns in the Location
          section get enough room to display truncated option labels
          without cramping. flex-shrink-0 stops the flex-1 backdrop from
          stealing width from the panel. */}
      <div
        className="w-full sm:w-[780px] flex-shrink-0 flex flex-col bg-[#F5F5F5]"
        style={{ boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', animation: 'slideInRight 0.25s ease-out' }}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-[#1E293B]">
            {isEdit ? 'Edit Lead' : 'Add New Lead'}
          </h2>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#1E293B] transition-colors px-2 py-1 rounded hover:bg-gray-100"
          >
            <X className="h-4 w-4" /> Close
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-5 space-y-4">

          {/* ── Section 1: Contact Information ── */}
          <div className="bg-white rounded-xl p-5">
            <h3 className={SECTION_TITLE}>Contact Information</h3>
            <div className="space-y-4">
              {/* First & Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>First Name <span className="text-red-500">*</span></label>
                  <input className={INPUT} placeholder="John" value={form.firstName}
                    onChange={e => set('firstName', e.target.value)} />
                </div>
                <div>
                  <label className={LABEL}>Last Name</label>
                  <input className={INPUT} placeholder="Doe" value={form.lastName}
                    onChange={e => set('lastName', e.target.value)} />
                </div>
              </div>

              {/* Emails + Phones — side by side on sm+ so the form feels
                  denser. Each column independently supports multi-value
                  with its own Add button. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Emails */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={LABEL}>Email(s)</label>
                    <button type="button" onClick={addEmail}
                      className="flex items-center gap-1 text-xs text-[#6366F1] font-medium hover:underline">
                      <Plus className="h-3 w-3" /> Add Email
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.emails.map((email: string, i: number) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          type="email"
                          className={`${INPUT} flex-1`}
                          placeholder={i === 0 ? 'Primary email' : 'Alternate email'}
                          value={email}
                          onChange={e => updateEmail(i, e.target.value)}
                        />
                        {i > 0 && (
                          <button type="button" onClick={() => removeEmail(i)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phones */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={LABEL}>Phone(s)</label>
                    <button type="button" onClick={addPhone}
                      className="flex items-center gap-1 text-xs text-[#6366F1] font-medium hover:underline">
                      <Plus className="h-3 w-3" /> Add Phone
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.phones.map((phone: any, i: number) => (
                      <div key={i} className="flex gap-2 items-center">
                        <div className="flex-1 min-w-0">
                          <PhoneInput
                            value={{ countryCode: phone.countryCode, number: phone.number }}
                            onChange={val => {
                              updatePhone(i, { countryCode: val.countryCode, number: val.number });
                            }}
                            placeholder={i === 0 ? 'Primary phone' : 'Alternate phone'}
                          />
                        </div>
                        {i > 0 && (
                          <button type="button" onClick={() => removePhone(i)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 2: Location Information ── */}
          <div className="bg-white rounded-xl p-5">
            <h3 className={SECTION_TITLE}>Location Information</h3>
            <div className="space-y-4">
              {/* Address */}
              <div>
                <label className={LABEL}>Address</label>
                <input className={INPUT} placeholder="Street address" value={form.address}
                  onChange={e => set('address', e.target.value)} />
              </div>

              {/* Geo error banner */}
              {geoError && (
                <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  {geoError}
                </div>
              )}

              {/* Row 1: Country | State | City — stacks on mobile, 3-col on sm+ */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={LABEL}>Country</label>
                  <SearchableSelect
                    options={countries}
                    value={form.country}
                    onChange={handleCountryChange}
                    placeholder={loadingCountries ? 'Loading...' : 'Select Country'}
                    loading={loadingCountries}
                    disabled={loadingCountries}
                  />
                </div>

                <div>
                  <label className={LABEL}>
                    State
                    {loadingStates && <Loader2 className="inline ml-1 h-3 w-3 animate-spin text-[#6366F1]" />}
                  </label>
                  <SearchableSelect
                    options={states}
                    value={form.state}
                    onChange={handleStateChange}
                    placeholder={
                      !form.country ? 'Select country first' :
                      loadingStates ? 'Loading...' :
                      states.length === 0 ? 'N/A' :
                      'Select State'
                    }
                    loading={loadingStates}
                    disabled={!form.country || loadingStates}
                  />
                </div>

                <div>
                  <label className={LABEL}>
                    City
                    {loadingCities && <Loader2 className="inline ml-1 h-3 w-3 animate-spin text-[#6366F1]" />}
                  </label>
                  {cities.length > 0 ? (
                    <SearchableSelect
                      options={cities}
                      value={form.city}
                      onChange={val => set('city', val)}
                      placeholder={
                        !form.state ? 'Select state first' :
                        loadingCities ? 'Loading...' :
                        'Select City'
                      }
                      loading={loadingCities}
                      disabled={!form.state || loadingCities}
                    />
                  ) : (
                    <input
                      className={INPUT}
                      placeholder={!form.state ? 'Select state first' : 'Type city'}
                      value={form.city}
                      onChange={e => set('city', e.target.value)}
                      disabled={!form.state && !loadingCities}
                    />
                  )}
                </div>
              </div>

              {/* Row 2: Zipcode — narrow, left aligned */}
              <div style={{ width: '150px' }}>
                <label className={LABEL}>Zipcode</label>
                <input
                  className={INPUT}
                  placeholder="400001"
                  value={form.pincode}
                  onChange={e => set('pincode', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Section 3: Lead Information ── */}
          <div className="bg-white rounded-xl p-5">
            <h3 className={SECTION_TITLE}>Lead Information</h3>
            <div className="space-y-4">
              {/* Source & Assign To */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Lead Source</label>
                  <SelectField className={SELECT} value={form.source} onChange={e => set('source', e.target.value)}>
                    <option value="">Select source</option>
                    {/* Include the existing source as an option if it's not
                        in the loaded list (e.g. a custom source set via API
                        or a source that was later deactivated). Keeps the
                        field selected on open instead of silently clearing. */}
                    {form.source && !sourceOptions.includes(form.source) && (
                      <option value={form.source}>{form.source}</option>
                    )}
                    {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </SelectField>
                </div>
                <div>
                  <label className={LABEL}>Assign To</label>
                  <SelectField className={SELECT} value={form.assignedToId} onChange={e => set('assignedToId', e.target.value)}>
                    <option value="">Unassigned</option>
                    {/* Inject the currently-assigned user if they're not in
                        the loaded list (deactivated, out of scope, or the
                        users endpoint is slow). Prevents silent
                        "Unassigned" display on a genuinely assigned lead. */}
                    {form.assignedToId && !users.some(u => u.id === form.assignedToId) && lead?.assignedTo && (
                      <option value={form.assignedToId}>
                        {lead.assignedTo.name}{lead.assignedTo.isActive === false ? ' (inactive)' : ''}
                      </option>
                    )}
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </SelectField>
                </div>
              </div>

              {/* Category & Color */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Lead Category</label>
                  <SelectField className={SELECT} value={form.leadCategory} onChange={e => set('leadCategory', e.target.value)}>
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </SelectField>
                </div>
                <div>
                  <label className={LABEL}>Lead Color</label>
                  <SelectField className={SELECT} value={form.leadColor} onChange={e => set('leadColor', e.target.value)}>
                    <option value="">Select color</option>
                    {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </SelectField>
                </div>
              </div>

              {/* Status & Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Lead Status</label>
                  <SelectField className={SELECT} value={form.status} onChange={e => set('status', e.target.value)}>
                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </SelectField>
                </div>
                <div>
                  <label className={LABEL}>Priority</label>
                  <SelectField className={SELECT} value={form.priority} onChange={e => set('priority', e.target.value)}>
                    <option value="">Select priority</option>
                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </SelectField>
                </div>
              </div>

              {/* Hashtags */}
              <div className="rounded-xl bg-[#EEF2FF] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Hash className="h-4 w-4 text-[#6366F1]" />
                  <span className="text-sm font-semibold text-[#6366F1]">Hashtags</span>
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    className={`${INPUT} flex-1`}
                    placeholder="Add hashtag (without #)"
                    value={hashtagInput}
                    onChange={e => setHashtagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHashtag(); } }}
                  />
                  <button type="button" onClick={addHashtag}
                    className="h-10 w-10 flex items-center justify-center rounded-lg bg-[#6366F1] text-white hover:bg-[#4F46E5] transition-colors flex-shrink-0">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {form.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.hashtags.map((tag: string) => (
                      <span key={tag}
                        className="flex items-center gap-1 bg-white text-[#6366F1] text-xs font-medium px-2.5 py-1 rounded-full border border-[#6366F1]/20">
                        #{tag}
                        <button type="button" onClick={() => removeHashtag(tag)}
                          className="text-[#6366F1]/50 hover:text-[#6366F1] ml-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Description / Notes */}
              <div>
                <label className={LABEL}>Description</label>
                <textarea
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-gray-400"
                  placeholder="Notes or description about this lead..."
                  value={form.notes}
                  onChange={e => set('notes', e.target.value)}
                  rows={3}
                />
              </div>

              {/* UTM (collapsible) */}
              <div className="border border-gray-100 rounded-lg overflow-hidden">
                <button type="button" onClick={() => setShowUtm(v => !v)}
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-gray-50 text-xs font-medium text-[#64748B] hover:bg-gray-100 transition-colors">
                  <span>UTM Parameters (optional)</span>
                  {showUtm ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                {showUtm && (
                  <div className="p-4 grid grid-cols-2 gap-3">
                    {[
                      { key: 'campaignName', label: 'Campaign Name', ph: 'Summer 2025' },
                      { key: 'utmSource', label: 'utm_source', ph: 'google' },
                      { key: 'utmMedium', label: 'utm_medium', ph: 'cpc' },
                      { key: 'utmCampaign', label: 'utm_campaign', ph: 'summer_sale' },
                      { key: 'utmTerm', label: 'utm_term', ph: 'running shoes' },
                      { key: 'utmContent', label: 'utm_content', ph: 'logolink' },
                    ].map(f => (
                      <div key={f.key}>
                        <label className={LABEL}>{f.label}</label>
                        <input className={INPUT} placeholder={f.ph} value={(form as any)[f.key]}
                          onChange={e => set(f.key, e.target.value)} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 4: Custom Fields ── */}
          {customFieldDefs.length > 0 && (
            <div className="bg-white rounded-xl p-5">
              <h3 className={SECTION_TITLE}>Custom Fields</h3>
              <div className="space-y-4">
                {customFieldDefs.map(field => {
                  const val = customFieldValues[field.id];
                  const setVal = (v: any) => setCustomFieldValues(p => ({ ...p, [field.id]: v }));
                  const opts: string[] = (field.keyValues ?? []).map((k: any) =>
                    typeof k === 'string' ? k : (k.value ?? k.label ?? String(k))
                  );
                  return (
                    <div key={field.id}>
                      <label className={LABEL}>
                        {field.label}
                        {field.isRequired && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      {field.fieldType === 'text' && (
                        <input className={INPUT} value={val ?? ''} onChange={e => setVal(e.target.value)} />
                      )}
                      {field.fieldType === 'number' && (
                        <input type="number" className={INPUT} value={val ?? ''} onChange={e => setVal(e.target.value)} />
                      )}
                      {field.fieldType === 'long_text' && (
                        <textarea rows={3} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-gray-400"
                          value={val ?? ''} onChange={e => setVal(e.target.value)} />
                      )}
                      {field.fieldType === 'date' && (
                        <DateInput className="w-full h-10" value={val ?? ''} onChange={v => setVal(v)} />
                      )}
                      {field.fieldType === 'dropdown' && (
                        <SelectField className={SELECT} value={val ?? ''} onChange={e => setVal(e.target.value)}>
                          <option value="">Select…</option>
                          {opts.map(o => <option key={o} value={o}>{o}</option>)}
                        </SelectField>
                      )}
                      {field.fieldType === 'checkbox' && (
                        <button type="button" onClick={() => setVal(!val)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${val ? 'bg-[#6366F1]' : 'bg-slate-200'}`}>
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${val ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      )}
                      {field.fieldType === 'multi-select' && (
                        <div className="grid grid-cols-2 gap-1.5 mt-1">
                          {opts.map(o => {
                            const checked = Array.isArray(val) && val.includes(o);
                            return (
                              <label key={o} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                <input type="checkbox" checked={checked}
                                  onChange={() => setVal(checked ? val.filter((x: string) => x !== o) : [...(val ?? []), o])}
                                  className="h-3.5 w-3.5 rounded border-slate-300 text-[#6366F1] accent-[#6366F1]" />
                                {o}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 border border-red-200 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="text-sm text-[#64748B] hover:text-[#1E293B] font-medium transition-colors px-4 py-2">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#6366F1] text-white text-sm font-semibold hover:bg-[#4F46E5] disabled:opacity-60 transition-colors"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Lead'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>

      {duplicates.length > 0 && (
        <DuplicateWarningModal
          duplicates={duplicates}
          onCreateAnyway={() => {
            setDuplicates([]);
            handleSubmit();
          }}
          onClose={() => { setDuplicates([]); setPendingPayload(null); }}
        />
      )}
    </div>
  );
}
